import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Stack,
  Typography,
  Button,
  Collapse,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";

export type Service = {
  id: string;
  serviceTypeName: string;
  serviceTypeDescription: string;
  serviceTypePrice: number; // dollars
  duration: number; // minutes
  serviceCategory?: string;
};

type Props = {
  services: Service[];
  selected: string[];
  onToggle: (serviceId: string) => void;
};

const currency = (n?: number) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD" })
    : "$0.00";

const formatDuration = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h} hr ${m} min`;
  if (h) return `${h} hr`;
  return `${m} min`;
};

const displayCategory = (raw?: string) => {
  const t = (raw ?? "").trim();
  return t.length ? t : "General services";
};

export default function ServiceSelector({ services, selected, onToggle }: Props) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = useCallback(
    (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] })),
    []
  );

  // Group + sort (memoized)
  const groups = useMemo(() => {
    const map = new Map<string, Service[]>();
    for (const s of services) {
      const cat = displayCategory(s.serviceCategory);
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    const entries = Array.from(map.entries()).sort((a, b) => {
      if (a[0] === "General services") return 1;
      if (b[0] === "General services") return -1;
      return a[0].localeCompare(b[0], undefined, { sensitivity: "base" });
    });
    return entries; // [category, Service[]][]
  }, [services]);

  const hasOnlyUncategorized =
    groups.length === 1 && groups[0][0] === "General services";

  // Summary totals
  const { count, totalPrice, totalMinutes } = useMemo(() => {
    let cnt = 0, price = 0, mins = 0;
    const set = new Set(selected);
    for (const s of services) {
      if (set.has(s.id)) {
        cnt++; price += s.serviceTypePrice || 0; mins += s.duration || 0;
      }
    }
    return { count: cnt, totalPrice: price, totalMinutes: mins };
  }, [services, selected]);

  return (
    <Box>
      {/* Summary */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color="primary"
            label={`${count} selected`}
            variant={count ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label={`${currency(totalPrice)} • ${formatDuration(totalMinutes)}`}
            variant="outlined"
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          maxWidth: 900,
          width: "100%",
          mx: "auto",
        }}
      >
        {groups.map(([category, items]) => {
          const selectedInCat = items.filter(i => selected.includes(i.id)).length;
          const allSelected = selectedInCat === items.length && items.length > 0;

          return (
            <Box key={category}>
              {/* Category header */}
              {!(hasOnlyUncategorized && category === "General services") && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                    {category}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        // select all in category not already selected
                        const notSelected = items.filter(i => !selected.includes(i.id));
                        notSelected.forEach(i => onToggle(i.id));
                      }}
                      disabled={allSelected}
                    >
                      Select all
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="secondary"
                      onClick={() => {
                        // clear all in category that are selected
                        items.filter(i => selected.includes(i.id))
                             .forEach(i => onToggle(i.id));
                      }}
                      disabled={!selectedInCat}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* Services list */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {items.map((s) => {
                  const isChecked = selected.includes(s.id);
                  const isExpanded = !!expanded[s.id];

                  const selectedStyles = isChecked
                    ? {
                        borderColor: theme.palette.accent.main,
                        borderWidth: 2,
                        backgroundColor: alpha(theme.palette.accent.main, 0.08),
                      }
                    : { borderColor: "divider", borderWidth: 1 };

                  return (
                    <Card
                      key={s.id}
                      variant="outlined"
                      sx={{
                        position: "relative",
                        ...selectedStyles,
                        transition: "border-color .15s ease, background-color .15s ease",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, isChecked ? 0.06 : 0.03),
                        },
                      }}
                      role="checkbox"
                      aria-checked={isChecked}
                    >
                      <CardActionArea
                        onClick={() => onToggle(s.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onToggle(s.id);
                          }
                        }}
                        sx={{ alignItems: "stretch" }}
                      >
                        {/* Right-top checkbox */}
                        <Checkbox
                          checked={isChecked}
                          onChange={() => onToggle(s.id)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ "aria-label": `${s.serviceTypeName} ${currency(s.serviceTypePrice)}` }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "background.paper",
                            borderRadius: 1
                          }}
                        />

                        <CardContent sx={{ pr: 7 }}>
                          <Stack spacing={1}>
                            {/* Row: title + price */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 2
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={700}>
                                {s.serviceTypeName}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                fontWeight={800}
                                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                              >
                                {currency(s.serviceTypePrice)}
                              </Typography>
                            </Box>

                            {/* Description with collapse */}
                            {!!s.serviceTypeDescription?.trim() && (
                              <>
                                <Collapse in={isExpanded} collapsedSize={56}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ whiteSpace: "pre-line" }}
                                  >
                                    {s.serviceTypeDescription}
                                  </Typography>
                                </Collapse>
                                {s.serviceTypeDescription.length > 120 && (
                                  <Button
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpand(s.id);
                                    }}
                                  >
                                    {isExpanded ? "Less" : "More"}
                                  </Button>
                                )}
                              </>
                            )}

                            <Chip
                              label={formatDuration(s.duration)}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ alignSelf: "flex-start" }}
                            />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
